/**
 * **`PastGathering` in `legacy/frontend/src/screens/SessionScreen.tsx` (`:227-595`), whole but for
 * its imports.** F-014 T9 ported the closed evening — its head, what it came to, the roster or the
 * line, «Состав проверен», and «Стереть …» with its question — and **F-024 T11** filled the holes it
 * left for the closed service's programme: `markingLine` with its doc comment (`:227-245`), the prop
 * `onOpenProgram` (`:259`, `:266`), the programme effect and `mark()` (`:281-313`), `program`,
 * `programFailure` and `busy` (`:321-331`), and the block with its door (`:447-513`). `FND-595`
 * (`KEEP`) scoped the absence to F-014 built alone; the block and its `GET /session/{id}/program`
 * are back. `SessionsScreen`'s `past` arm is its caller. The new tree has no `SessionScreen.tsx`, so
 * the evening sits under a name of its own, `AheadGathering.tsx`'s precedent, and the export keeps
 * legacy's form (`:254`).
 *
 * Everything below the imports is the old app's, comments included, byte for byte. The imports are
 * ours: the server shapes come from `@contracts/index` (ADR-0011) and the values from `../api`.
 * `ProgramScreen` (their `:34`) is imported by `SessionsScreen.tsx`, which draws it, not here.
 *
 * **What holds those lines to their original is `scripts/ported-partial.txt`.** Its row for this file
 * names `screens/SessionScreen.tsx` in a fifth field (`FND-083`), and `scripts/check-css-port.sh`
 * compares the registered run with legacy's, line for line.
 */
import { useEffect, useState } from 'react';

import type { ClosedSession, Program, SessionAttendance } from '@contracts/index';

import { ApiError, api } from '../api';
import { capitalised, formatTime, formatWeekdayDate, plural, words } from '../format';
import { ChevronRightIcon } from '../icons';
import { useBackKey } from '../backKey';
import Ask from './Ask';
import PastRoster from './PastRoster';
import { NOT_DONE, subLine } from './program';
import Unfold from './Unfold';

/**
 * What the block over the numbers says, and it is three sentences for three
 * readers.
 *
 * The regent and the keeper are told what marking costs — the song loses the
 * date — because otherwise they find that out on a different screen. An author
 * is told the window and whose the choir numbers are. Past the window nobody is
 * offered anything, so the line says why rather than leaving a list that has
 * quietly stopped answering.
 */
function markingLine(program: Program, canEdit: boolean): string {
  if (!canEdit) {
    return 'Прошло больше 14 дней после закрытия — это уже нельзя изменить.';
  }
  if (program.can_order) {
    return 'Всё это спели. Если что-то не пели — сними отметку, у песни это уберёт дату.';
  }
  return 'Свой номер можно поправить в течение 14 дней. Песни хора отмечает регент.';
}

/**
 * A gathering that is over: what it came to, and the roster underneath it.
 *
 * This is the pair the audit went after. The summary and the composition of one
 * evening stood on two different tabs, and the only thing joining them was a
 * variable in `App` and a button called «Проверить состав».
 */
export function PastGathering({
  item,
  isRegent,
  onBack,
  onChanged,
  onOpenProgram,
}: {
  item: ClosedSession;
  isRegent: boolean;
  onBack: () => void;
  /** An erased evening has to leave the schedule behind this screen too. */
  onChanged: () => void;
  onOpenProgram: () => void;
}) {
  /** The counts, which a correction below changes under the summary above.
   *
   * All three of them, and lateness is the one that was missing: it was read off
   * `item` - the card this screen was opened from - so a regent who set somebody
   * late on the roster below watched «0 опозданий» stay where it was until the
   * screen was left and entered again. Reported 2026-08-24. */
  const [counts, setCounts] = useState({
    present: item.present_count,
    members: item.member_count,
    late: item.late_count,
  });
  const absent = counts.members - counts.present;

  useEffect(() => {
    if (item.kind !== 'service') return;
    let dropped = false;
    void (async () => {
      try {
        const answer = await api.program(item.session_id);
        if (!dropped) setProgram(answer);
      } catch {
        // Silent: the programme is one block of an evening that is readable
        // without it, and a failure banner over a closed service would be
        // shouting about the least of what is on the screen.
      }
    })();
    return () => {
      dropped = true;
    };
  }, [item.kind, item.session_id]);

  /** «Не спели», and the same tap back. Optimistic in neither direction: the
      answer carries the whole programme, so the list lands from it. */
  async function mark(itemId: number, performed: boolean) {
    setBusy(true);
    setProgramFailure(null);
    try {
      setProgram(await api.setProgramItemPerformed(itemId, !performed));
    } catch (error) {
      setProgramFailure(
        error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
      );
    } finally {
      setBusy(false);
    }
  }
  /**
   * The roster as the server last sent it, kept for the one thing the summary
   * above cannot answer: what erasing this evening would actually destroy. The
   * question has to carry the figure — «вы уверены?» stops nobody — and the
   * figure is two numbers, not one, because they are owed to different people.
   */
  const [composition, setComposition] = useState<SessionAttendance | null>(null);
  /**
   * The programme of this service, when there is one. Read from the endpoint
   * the programme screen already uses rather than grown onto this screen's own
   * payload: it answers `can_order` and the per-row permission too, so two
   * screens cannot disagree about who may mark what.
   */
  const [program, setProgram] = useState<Program | null>(null);
  const [programFailure, setProgramFailure] = useState<string | null>(null);
  /** A mark in flight. Every mark on the block goes quiet together, because the
      answer relands the whole programme and a second tap would race it. */
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  useBackKey(onBack);
  const [erasing, setErasing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const said = words(item.kind);
  /**
   * Whether the machine's guesses are still unchecked on this evening.
   *
   * Its own state rather than `item.needs_review`, because the confirmation
   * happens on this screen and the card it was opened from is the list's copy:
   * without this the notice and the button would stand until the screen was
   * left and entered again.
   */
  const [unchecked, setUnchecked] = useState(item.needs_review);
  const [confirming, setConfirming] = useState(false);
  /**
   * Whether this screen is asking its reader to check the machine's guesses.
   *
   * `unchecked` is a fact about the evening; this is a fact about the reader,
   * and every notice about the review is the second one. When the history
   * opened to singers on 2026-09-07 the twin of this on «Расписание» was gated
   * and these were missed, so a singer opening a gathering the scheduler had
   * closed read «проверь состав — пока не подтвердишь, бот не напишет» with no
   * roster, no button and nothing he could do about it. One name for all three
   * so they cannot drift apart again.
   */
  const awaitingReview = isRegent && unchecked;

  /**
   * «Состав проверен» — and it is a real act rather than a courtesy.
   *
   * The flag gates the bot: until it comes off, nothing is said to anybody
   * about absences the scheduler wrote when it closed a rehearsal nobody
   * remembered to close. It used to come off inside the GET that fetches the
   * roster, which was fine while it only sorted the history — a screen opened
   * by mistake cannot be allowed to release a message to forty people.
   */
  async function confirmRoster(): Promise<void> {
    setConfirming(true);
    setFailure(null);
    try {
      await api.confirmRoster(item.session_id);
      setUnchecked(false);
      // The red tile on «Расписание» and the pill on «Хор» count this evening
      // until the list behind is told.
      onChanged();
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось подтвердить');
    } finally {
      setConfirming(false);
    }
  }

  /** What a person did, as against what the closing wrote for everyone else. */
  const marks =
    composition?.rows.filter((row) => row.status !== null && row.source !== 'system').length ?? 0;
  const machineAbsences =
    composition?.rows.filter((row) => row.source === 'system').length ?? 0;

  async function erase(): Promise<void> {
    setErasing(true);
    setFailure(null);
    try {
      await api.eraseSession(item.session_id);
      // The list behind this screen still holds the evening, and the screen
      // itself is now about something that does not exist.
      onChanged();
      onBack();
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось стереть');
      setErasing(false);
    }
  }

  return (
    <>
      <div className="screen-head">
        {/* `unchecked` and not `item.needs_review`: the confirmation happens on
            this screen, and `item` is the list's copy of the card it was opened
            from. This line was the one reader of the flag that never got
            switched over, so «Состав проверен» took away the notice and the
            button and left the date red - the screen saying «checked» and
            «overdue» at once until it was left and entered again. */}
        <p className={awaitingReview ? 'date-line date-line--overdue' : 'date-line'}>
          {formatWeekdayDate(item.effective_start)} ·{' '}
          <span className="num">{formatTime(item.effective_start)}</span> ·{' '}
          {words(item.kind).closedForm}
        </p>
        <h1 className="screen-title">{item.title ?? words(item.kind).titled}</h1>
      </div>

      {/* The same box the panel draws the moment a gathering is closed, and for
          the same reason: the figure is what an hour of standing at the door
          was for. Here it is the head of the evening's own screen rather than a
          card that has to be dismissed. */}
      <section className="summary card--toned card--toned--outline" data-tone="success">
        <p className="label label--tone">
          {words(item.kind).titled} {words(item.kind).closedForm}
        </p>
        <p className="summary-figure">
          <span className="figure figure--answer">{counts.present}</span>
          <span className="figure-of">из {counts.members}</span>
        </p>
        <p className="summary-detail">
          {counts.late} {plural(counts.late, ['опоздание', 'опоздания', 'опозданий'])} ·{' '}
          {absent} {plural(absent, ['пропуск', 'пропуска', 'пропусков'])}
        </p>
        {awaitingReview && (
          <p className="hint">
            Закрыло приложение, когда время вышло. Отсутствующих проставила машина — проверь
            состав. Пока не подтвердишь, бот об этих пропусках никому не напишет.
          </p>
        )}
      </section>

      {/* What the choir actually did, between what the evening came to and who
          was at it: one movement down the page. A service only - a rehearsal
          has no programme - and a list rather than the door it used to be,
          because now there is something to correct here. */}
      {item.kind === 'service' && program !== null && program.items.length > 0 && (
        <div className="block">
          <span className="label">
            Программа · <span className="section-count">{program.items.length}</span>
          </span>
          {/* The cost is named before the tap and not after it: otherwise the
              regent learns what marking did on a different screen. */}
          <p className="hint">{markingLine(program, item.can_edit)}</p>

          <ul className="live">
            {program.items.map((number) => (
              <li
                className={
                  number.performed ? 'live-row live-row--stack' : 'live-row live-row--stack live-row--unperformed'
                }
                key={number.item_id}
              >
                <span className="live-head">
                  <span className="program-n">{number.position}</span>
                  <span className="live-body">
                    <span className="program-title">
                      <span className="live-name">{number.title}</span>
                      {number.is_choir && (
                        <span className="answer answer--mark" data-tone="accent">
                          хор
                        </span>
                      )}
                    </span>
                    <span className="live-sub live-sub--wrap">{subLine(number)}</span>
                  </span>
                  {/* A row nobody may mark draws no mark rather than a disabled
                      one: grey means unchosen in this app, and a control that
                      cannot be pressed says so by not being there. */}
                  {number.can_mark_performed && (
                    <button
                      className={
                        number.performed
                          ? 'live-mark live-mark--faint'
                          : 'live-mark live-mark--picked'
                      }
                      data-tone={number.performed ? undefined : 'quiet'}
                      data-press="wash"
                      disabled={busy}
                      aria-pressed={!number.performed}
                      aria-label={`«${number.title}» ${NOT_DONE[number.kind]}`}
                      onClick={() => void mark(number.item_id, number.performed)}
                    >
                      {NOT_DONE[number.kind]}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {programFailure !== null && <p className="failure">{programFailure}</p>}

          <button className="btn btn--quiet btn--after-list" onClick={onOpenProgram}>
            Открыть программу
            <ChevronRightIcon size={16} quiet />
          </button>
        </div>
      )}

      {/* The roster is the regent's: the marks are his records about people,
          and a singer's own is the strip of the last ten on «Я». */}
      {isRegent ? (
        <PastRoster
          sessionId={item.session_id}
          start={item.effective_start}
          onEdited={(attendance) => {
            setComposition(attendance);
            setCounts({
              present: attendance.present_count,
              members: attendance.member_count,
              late: attendance.late_count,
            });
          }}
        />
      ) : (
        <p className="hint">Свои отметки за три месяца видно на «Я».</p>
      )}

      {/* Under the roster, because the roster is what is being confirmed: the
          regent reads down the marks and then says he has. Filled with the
          accent — on this screen it is what he came for, and it is the one
          thing standing between the choir and a message about absences a
          machine guessed. It goes when it is done and does not come back. */}
      {/* C-023 batch 2 (`FND-1618`): it grows in and folds away rather than appearing whole
          and going in one frame, which pulled «Стереть …» up under the finger that had just
          confirmed. */}
      <Unfold open={awaitingReview}>
        <div className="block">
          <button className="btn btn--primary" disabled={confirming} onClick={() => void confirmRoster()}>
            Состав проверен
          </button>
          <p className="hint">
            После этого бот напишет тем, кто остался с пропуском: «вчера на спевке тебя не
            было». Тем, кого ты поправил, — не напишет.
          </p>
        </div>
      </Unfold>

      {/* The way out of an evening that never happened, and it is the last
          thing on the screen because it is the last thing anybody should reach
          for: above it stand every correction that would have been enough.

          Offered on the same fourteen days as the corrections, and for the same
          reason — a record older than that is history, and this app does not
          edit history. Outlined and not filled: red belongs to the button
          inside the question, and until the question has been asked nothing
          dangerous has happened. */}
      {isRegent && composition !== null && composition.can_edit && (
        <div className="block">
          <p className="hint">
            {/* No pronoun for the gathering: «спевка» is feminine and «участие» is
                neuter, and one sentence with a substituted noun cannot agree with
                both — the defect task 7 went through fifteen strings to remove. */}
            {capitalised(said.genitive)} не было — открыли по ошибке или отменили? Закрытие
            уже записало пропуски всем, кто не пришёл. Сотри {said.accusative} вместе с
            отметками, и в истории не останется ни того, ни другого.
          </p>
          <button
            className="btn btn--outline btn--danger-quiet"
            disabled={erasing}
            onClick={() => setAsking(true)}
          >
            Стереть {said.accusative}
          </button>
        </div>
      )}

      <Ask
        open={asking}
        title={`Стереть ${said.accusative} без следа?`}
        cost={
          <>
            Исчезнут {marks} {plural(marks, ['отметка', 'отметки', 'отметок'])} и{' '}
            {machineAbsences}{' '}
            {plural(machineAbsences, ['пропуск', 'пропуска', 'пропусков'])}, которые записало
            закрытие. {capitalised(said.nominative)} уйдёт из расписания, из процентов и из
            счёта пропусков подряд. Вернуть будет нечем.
          </>
        }
        verb="Стереть"
        busy={erasing}
        failure={failure}
        onConfirm={() => void erase()}
        onClose={() => setAsking(false)}
      />
    </>
  );
}
